import { useState } from "react"

import { Alert, App, Flex } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  useAgentPlatform,
  useDisconnectConfirm
} from "@/pages/DeploymentsPage/application/useAgentPlatform"
import { DEFAULT_COPILOT_STUDIO_SETTINGS } from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionSettingsModal,
  getCloudConnectionName
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"
import { ConnectedThreatDetection } from "./ConnectedThreatDetection"
import { COPILOT_STUDIO_NAME } from "./CopilotStudioConnection.constants"
import { CopilotStudioSettingsFields } from "./CopilotStudioSettingsFields"

import type { CopilotStudioSettings } from "@/api/useDeployments.types"
import type { CopilotStudioSettingsModalProps } from "."

// The environments, in any order
const environmentsKey = (environmentIds: string[] | undefined) =>
  JSON.stringify([...(environmentIds || [])].sort())

// A Microsoft tenant's connection: its app, its settings to edit and its
// threat detection steps. Saving waits for a change.
export const CopilotStudioSettingsModal = ({
  connection,
  threatDetection,
  onClose
}: CopilotStudioSettingsModalProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.copilotStudio"
  })
  const { t: tConnection } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection"
  })
  const { message } = App.useApp()
  const platform = useAgentPlatform("copilot-studio")
  const disconnect = useDisconnectConfirm("copilot-studio")
  const [settings, setSettings] = useState<CopilotStudioSettings>({
    ...DEFAULT_COPILOT_STUDIO_SETTINGS,
    ...connection.settings
  })
  const name = getCloudConnectionName(COPILOT_STUDIO_NAME, connection)
  const changed =
    environmentsKey(connection.settings?.environmentIds) !==
    environmentsKey(settings.environmentIds)

  const onSave = async () => {
    const saved = await platform.update(connection.connectionId, settings)

    if (!saved) return

    message.success(tConnection("updated", { name }))
    onClose()
  }

  return (
    <CloudConnectionSettingsModal
      name={name}
      identity={[
        { label: t("fields.tenantId"), value: connection.azureTenantId },
        { label: t("fields.clientId"), value: connection.clientId }
      ]}
      saveDisabled={!changed}
      isSaving={platform.isUpdating}
      onSave={onSave}
      onDisconnect={() => disconnect(connection.connectionId, name)}
      onClose={onClose}
    >
      <Flex vertical gap={16}>
        <CopilotStudioSettingsFields value={settings} onChange={setSettings} />
        <ConnectedThreatDetection
          connection={connection}
          {...threatDetection}
        />
        {platform.error && (
          <Alert
            showIcon
            type="error"
            title={platform.error}
            closable={{ onClose: platform.clearError }}
          />
        )}
      </Flex>
    </CloudConnectionSettingsModal>
  )
}
